import classNames from 'classnames';
import { Button } from 'reactstrap';

import { PropsWithChildren, useMemo } from 'react';

/**
 * Base button props type that extends HTML button attributes
 */
type ButtonPropsWithChildren = PropsWithChildren<
    React.ButtonHTMLAttributes<HTMLButtonElement>
>

/**
 * Props interface for the JWButton component
 * @property onClick - Optional click handler function
 * @property className - Additional CSS classes to apply
 * @property shape - Button shape variant ('default' | 'pill' | 'ghost')
 * @property variant - Button style variant ('default' | 'icon' | 'outlined' | 'ghost')
 * @property size - Button size variant ('large' | 'medium' | 'small')
 * @property color - Button color (defaults to 'purple')
 * @property Icon - Optional icon component to display
 * @property disabled - Whether the button is disabled
 */
interface JWButtonProps extends ButtonPropsWithChildren {
    onClick?: () => void
    className?: string
    shape?: 'default' | 'pill' | 'ghost'
    variant?: 'default' | 'icon' | 'outlined' | 'ghost'
    size?: 'large' | 'medium' | 'small'
    color?: string
    Icon?: any
    disabled?: boolean
}

/**
 * JWButton Component
 *
 * A customizable button component that supports various shapes, sizes, and variants.
 * Built on top of reactstrap's Button component with additional styling and functionality.
 *
 * @example
 * ```tsx
 * // Default button
 * <JWButton>Click me</JWButton>
 *
 * // Icon button
 * <JWButton variant="icon" Icon={IconComponent}>Icon Button</JWButton>
 *
 * // Outlined button
 * <JWButton variant="outlined" color="purple">Outlined Button</JWButton>
 *
 * // Ghost button
 * <JWButton variant="ghost" shape="pill">Ghost Button</JWButton>
 * ```
 */
const JWButton = (props: JWButtonProps) => {
    // Destructure props with default values
    const {
        className = '',
        shape = 'default',
        size = 'large',
        variant = 'default',
        color = 'purple',
        Icon,
        disabled = false
    } = props;

    /**
     * Generate shape-specific class names
     * @returns Array of Tailwind classes for button shape
     */
    const shapeClassNames = useMemo(() => {
        if (shape === 'pill') {
            return ['rounded-full'];
        }
        return ['rounded-md'];
    }, [shape]);

    /**
     * Generate size-specific class names
     * @returns Array of Tailwind classes for button size
     */
    const sizeClassNames = useMemo(() => {
        switch (size) {
        case 'small':
            return ['h-6', 'text-xs'];
        case 'medium':
            return ['h-8'];
        case 'large':
            return ['h-10'];
        default:
            return [];
        }
    }, [size]);

    /**
     * Generate variant-specific class names
     * @returns Array of Tailwind classes for button variant
     */
    const variantClassNames = useMemo(() => {
        if (variant === 'icon') {
            return [
                'p-1',
                'bg-transparent',
                'hover:bg-white',
                'hover:bg-opacity-25',
            ];
        }

        if (variant === 'outlined') {
            return [
                'p-4',
                'bg-transparent',
                `text-${color} font-bold`,
                `border-2 border-${color}`,
                `hover:bg-${color} hover:bg-opacity-10`,
            ];
        }

        if (variant === 'ghost') {
            return [
                'bg-transparent',
                'text-gray-400',
                'py-1',
                'px-2',
                'hover:bg-gray-200',
                'hover:text-gray-700',
            ];
        }

        return ['text-white text-lg', `bg-${color}`, 'p-2'];
    }, [variant]);

    /**
     * Generate icon-specific class names based on button size
     * @returns Array of Tailwind classes for icon sizing
     */
    const iconClassnames = useMemo(() => {
        switch (size) {
        case 'small':
            return ['h-4 w-4'];
        case 'medium':
            return ['h-5 w-5'];
        case 'large':
            return ['h-7 w-7'];
        default:
            return [];
        }
    }, [size]);

    return (
        <Button
            color={props.color}
            className={classNames(
                // Base classes
                ['flex', 'justify-center', 'items-center', 'font-nunito', 'transition-all', 'duration-200'],
                // Dynamic classes
                shapeClassNames,
                variantClassNames,
                sizeClassNames,
                // Custom classes
                className,
                // Disabled state
                { 'opacity-50 cursor-not-allowed': disabled }
            )}
            onClick={props.onClick}
            disabled={disabled}
        >
            {/* Icon rendering for non-icon variants */}
            {Icon && variant !== 'icon' && (
                <Icon className={classNames(iconClassnames, 'mr-2')} />
            )}
            {/* Icon rendering for icon variant */}
            {Icon && variant === 'icon' && (
                <Icon className={classNames(iconClassnames)} />
            )}
            {/* Button content */}
            {props.children}
        </Button>
    );
};

export default JWButton;
